'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/social/sidebar-styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { EmailRoundedIcon, FacebookRoundedIcon, GitHubIcon, InstagramIcon, LinkedInIcon, PhoneRoundedIcon, XIcon } from 'BomdisoftComponents/icons/index';
import { ImageWithFallback } from 'BomdisoftComponents/image-with-fallback/index';
import { socialIcons } from 'BomdisoftComponents/social/render';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { SocialAccount } from 'BomdisoftComponents/types';
import { callNumberT, email, getLanguageFromLocale, phoneT, sendEmailToT, socialMediaLinksT, visitBrandT } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import type { ReactNode } from 'react';

export type SocialSidebarProps = {
  accounts: SocialAccount;
  className?: string;
  iconClassName?: string;
  useImageIcons?: boolean;
};

export { socialIcons };

type SidebarItemProps = {
  href: string;
  label: string;
  tooltip: ReactNode;
  brand: string;
  external?: boolean;
  children: ReactNode;
};

const SidebarItem = ({ href, label, tooltip, brand, external = true, children }: SidebarItemProps) => (
  <Tooltip placement='right' title={tooltip}>
    <a aria-label={label} className='bomdisoft-social-sidebar-item' data-brand={brand} href={href} rel={external ? 'noopener noreferrer' : undefined} target={external ? '_blank' : undefined}>
      {children}
    </a>
  </Tooltip>
);

export const SocialSidebar = ({ accounts, className, iconClassName, useImageIcons }: SocialSidebarProps) => {
  const params = useParams();
  const language = getLanguageFromLocale(typeof params?.lang === 'string' ? params.lang : undefined);
  const visit = (name: string) => visitBrandT[language].replace('{name}', name);

  return (
    <nav aria-label={socialMediaLinksT[language]} className={joinClasses('bomdisoft-social-sidebar', className)}>
      {accounts.github ? (
        <SidebarItem brand='github' href={accounts.github} label={visit('GitHub')} tooltip='GitHub'>
          {useImageIcons ? <ImageWithFallback alt='' fallbackIcon={<GitHubIcon className={iconClassName} />} height={24} priority src={socialIcons.github} width={24} /> : <GitHubIcon className={iconClassName} />}
        </SidebarItem>
      ) : null}
      {accounts.linkedin ? (
        <SidebarItem brand='linkedin' href={accounts.linkedin} label={visit('LinkedIn')} tooltip='LinkedIn'>
          {useImageIcons ? <ImageWithFallback alt='' fallbackIcon={<LinkedInIcon className={iconClassName} />} height={24} priority src={socialIcons.linkedin} width={24} /> : <LinkedInIcon className={iconClassName} />}
        </SidebarItem>
      ) : null}
      {accounts.x ? (
        <SidebarItem brand='x' href={accounts.x} label={visit('X (Twitter)')} tooltip='X'>
          {useImageIcons ? <ImageWithFallback alt='' fallbackIcon={<XIcon className={iconClassName} />} height={24} priority src={socialIcons.x} width={24} /> : <XIcon className={iconClassName} />}
        </SidebarItem>
      ) : null}
      {accounts.instagram ? (
        <SidebarItem brand='instagram' href={accounts.instagram} label={visit('Instagram')} tooltip='Instagram'>
          {useImageIcons ? <ImageWithFallback alt='' fallbackIcon={<InstagramIcon className={iconClassName} />} height={24} priority src={socialIcons.instagram} width={24} /> : <InstagramIcon className={iconClassName} />}
        </SidebarItem>
      ) : null}
      {accounts.facebook ? (
        <SidebarItem brand='facebook' href={accounts.facebook} label={visit('Facebook')} tooltip='Facebook'>
          {useImageIcons ? <ImageWithFallback alt='' fallbackIcon={<FacebookRoundedIcon className={iconClassName} />} height={24} priority src={socialIcons.facebook} width={24} /> : <FacebookRoundedIcon className={iconClassName} />}
        </SidebarItem>
      ) : null}
      {accounts.tiktok ? (
        <SidebarItem brand='tiktok' href={accounts.tiktok} label={visit('TikTok')} tooltip='TikTok'>
          <ImageWithFallback alt='' height={24} priority src={socialIcons.tiktok} width={24} />
        </SidebarItem>
      ) : null}
      {accounts.email ? (
        <SidebarItem brand='email' external={false} href={`mailto:${accounts.email}`} label={sendEmailToT[language].replace('{email}', accounts.email)} tooltip={email[language]}>
          {useImageIcons ? <ImageWithFallback alt='' fallbackIcon={<EmailRoundedIcon className={iconClassName} />} height={24} priority src={socialIcons.email} width={24} /> : <EmailRoundedIcon className={iconClassName} />}
        </SidebarItem>
      ) : null}
      {accounts.mobile ? (
        <SidebarItem brand='mobile' external={false} href={`tel:${accounts.mobile}`} label={callNumberT[language].replace('{number}', accounts.mobile)} tooltip={phoneT[language]}>
          <PhoneRoundedIcon className={iconClassName} />
        </SidebarItem>
      ) : null}
    </nav>
  );
};
